<template>
  <v-select
      ref="users-select"
      :class="{'small': small}"
      v-bind:options="users"
      :clearable="clearable"
      :multiple="multiple"
      :reduce="u=>u.id"
      label="id"
      v-bind:appendToBody="appendToBody"
      v-model="selected"
      :disabled="disabled"
      :filterable="false"
      @search="onSearch"
  >
    <template #option="user">
      <div class="text-capitalize">
        <user-avatar size="25" variant="info" :userId="user.id"></user-avatar>
        {{ getUserFullName(user.id) }}
      </div>
    </template>
    <template #selected-option="user">
      <div class="text-capitalize">
        <user-avatar size="15" variant="info" :userId="user.id"></user-avatar>
        {{ getUserFullName(user.id) }}
      </div>
    </template>
  </v-select>
</template>

<script>
import { mapGetters } from 'vuex'
import UserAvatar from '@/components/widget/UserAvatar'

export default {
  name: 'userPicker',
  components: { UserAvatar },
  props: {
    value: {
      required: true,
    },
    small: {
      type: Boolean,
      default: () => false,
    },
    selectProps: {
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    clearable: {
      type: Boolean,
      default: () => false,
    },
    multiple: {
      type: Boolean,
      default: () => false,
    },
    excludeMe: {
      type: Boolean,
      default: () => false,
    },
    appendToBody: {
      type: Boolean,
      default: () => false,
    },
    // ✅ Both contexts supported
    clientId: {
      type: String,
      default: null,
    },
    monetaryId: {
      type: String,
      default: null,
    },
    userType: {
      type: Number,
      default: null,
    },
    nullable: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    search: '',
    users: [],
  }),
  computed: {
    ...mapGetters('user', ['myId']),
    ...mapGetters('data', ['allUsers', 'getUserFullName']),
    selected: {
      get() {
        return this.value
      },
      set(value) {
        if (value !== this.value && value != null) {
          // ✅ Handle both client & monetary context
          if (this.clientId) {
            this.updateRepresentativeId(this.clientId, value)
          } else if (this.monetaryId) {
            this.updateDepositorId(this.monetaryId, value)
          }
        }
        this.$emit('input', value)
      },
    },
  },
  created() {
    this.refreshUsers()
  },
  methods: {
    refreshUsers() {
      let users = this.allUsers.map(u => ({
        ...u,
        name: u.firstName + ' ' + (u.lastName || ''),
        value: u.id,
      }))

      if (this.excludeMe) {
        users = users.filter(u => u.id !== this.myId)
      }

      if (this.search && this.search.trim() !== '') {
        const words = this.search.trim().toLowerCase().split(' ')
        users = users.filter(u => {
          // Always include selected user(s)
          if (Array.isArray(this.selected) && this.selected.includes(u.id)) return true
          if (!Array.isArray(this.selected) && this.selected === u.id) return true

          // Match search
          return words.some(
              w =>
                  (u.firstName || '').toLowerCase().includes(w) ||
                  (u.lastName || '').toLowerCase().includes(w) ||
                  (u.email || '').toLowerCase().includes(w)
          )
        })
      }

      if (this.nullable) {
        users.unshift({
          id: null,
          value: null,
          name: 'NOT ASSIGNED',
        })
      }

      this.users = users
    },

    updateRepresentativeId(clientId, userId) {
      this.$emit('update', {
        clientId,
        userId,
        userType: this.userType,
      })
    },

    updateDepositorId(monetaryId, userId) {
      this.$emit('update', {
        monetaryId,
        userId,
      })
    },

    onSearch(search, loading) {
      loading(true)
      this.search = search
      this.refreshUsers()
      loading(false)
    },
  },
}
</script>

<style scoped>
.small {
  font-size: 0.85rem;
}
</style>
